import apiClient, { API_ENDPOINTS, appendPath, dispatchAndResolve, executeApiRequest, rejectWithError } from "api";
import keys from "config/keys.jsx";
import {
  buildInvoiceByOrderCriteria,
  buildOrderUpdateEndpoint,
  buildPaidOrdersCriteria,
  buildPrimaryIdCriteria,
  normalizeOrderByIdCriteria
} from "domains/commerce/services/orderRequestBuilders";
import * as types from "./types.jsx";

const { event, invoice, order, scheduledProducts } = API_ENDPOINTS;

const rejectOrderError = (error, label) => rejectWithError(error, label);

export const getOrderById = (id, companyId) => async (dispatch) => {
  try {
    const data = await apiClient.post(order.getOrderById, normalizeOrderByIdCriteria(id, companyId));
    return dispatchAndResolve(dispatch, types.GET_ORDER_BY_ID, data);
  } catch (error) {
    return rejectOrderError(error, "Error getting order by id");
  }
};

export const updateOrder = (orderPayload, updateType = "") => async (dispatch) => {
  try {
    const data = await apiClient.post(
      buildOrderUpdateEndpoint(order.updateOrder, updateType),
      orderPayload
    );
    return dispatchAndResolve(dispatch, types.UPDATE_ORDER, data);
  } catch (error) {
    return rejectOrderError(error, "Error updating order");
  }
};

export const publishAvpsEvent = (avpsEvent) => async () => {
  try {
    return await executeApiRequest(keys.EVENT_URL, event.publishAvpsEvent, {
      method: "POST",
      payload: avpsEvent,
      responseType: "text",
      credentials: "same-origin",
      skipAuth: true
    });
  } catch (error) {
    return rejectOrderError(error, "Error publishing AVPS event");
  }
};

export const getOrdersByOrderId = (primaryId) => async (dispatch) => {
  try {
    const data = await apiClient.post(order.getOrdersById, buildPrimaryIdCriteria(primaryId));
    return dispatchAndResolve(dispatch, types.GET_ORDER_BY_ID, data);
  } catch (error) {
    return rejectOrderError(error, "Error getting orders by order id");
  }
};

export const getAllOrdersByAccountId = (payload) => async (dispatch) => {
  try {
    const data = await apiClient.post(order.getOrdersById, payload);
    return dispatchAndResolve(dispatch, types.GET_ORDER_BY_ID, data);
  } catch (error) {
    return rejectOrderError(error, "Error getting orders by account id");
  }
};

export const getSchedulesByUserId = (userId) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(scheduledProducts.getSchedulesByUserId, userId));
    return dispatchAndResolve(dispatch, types.GET_USER_SCHEDULED_PRODUCTS, data);
  } catch (error) {
    return rejectOrderError(error, "Error getting schedules by user id");
  }
};

export const getAllOrders = (accountId, status = "") => async (dispatch) => {
  try {
    const data = status === ""
      ? await apiClient.get(appendPath(order.getAllOrdersByAccountId, accountId))
      : await apiClient.get(`${order.getAllOrdersByAccountStatus}/`, {
        query: {
          accountId,
          status
        }
      });

    return dispatchAndResolve(dispatch, types.GET_ALL_ORDERS_BY_ACCOUNT_STATUS, data);
  } catch (error) {
    return rejectOrderError(error, "Error getting all orders");
  }
};

export const getInvoices = (userId) => async (dispatch) => {
  try {
    if (!userId) {
      throw new Error("userId is required but was not provided");
    }

    const data = await apiClient.get(order.getInvoices, { query: { userId } });
    return dispatchAndResolve(dispatch, types.GET_INVOICES, data);
  } catch (error) {
    return rejectOrderError(error, "Error fetching invoices");
  }
};

export const getInvoicesByOrderId = (accountId, orderId) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      invoice.findByCriteria,
      buildInvoiceByOrderCriteria(accountId, orderId)
    );
    return dispatchAndResolve(dispatch, types.GET_INVOICES, data);
  } catch (error) {
    return rejectOrderError(error, "Error fetching invoices by order id");
  }
};

export const findOrders = (primaryId, companyPrimaryId, currentStatus = "PAID", parentCompanyId) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      order.orderFind,
      buildPaidOrdersCriteria(primaryId, companyPrimaryId, currentStatus)
    );

    const responseValue = data?.payload?.data ?? data?.data ?? data;
    if (
      Array.isArray(responseValue) &&
      responseValue.length === 0 &&
      parentCompanyId &&
      String(parentCompanyId) !== String(companyPrimaryId)
    ) {
      const parentData = await apiClient.post(
        order.orderFind,
        buildPaidOrdersCriteria(primaryId, parentCompanyId, currentStatus)
      );
      return dispatchAndResolve(dispatch, types.GET_ORDER_BY_ID, parentData);
    }

    return dispatchAndResolve(dispatch, types.GET_ORDER_BY_ID, data);
  } catch (error) {
    return rejectOrderError(error, "Error finding orders");
  }
};

export const createOrders = (orders) => async (dispatch) => {
  try {
    const data = await apiClient.post(order.createOrders, orders);
    return dispatchAndResolve(dispatch, types.CREATE_ORDERS, data);
  } catch (error) {
    return rejectOrderError(error, "Error creating orders");
  }
};

export const orderReversal = (orderPayload) => async (dispatch) => {
  try {
    const data = await apiClient.post(order.orderReversal, orderPayload);
    return dispatchAndResolve(dispatch, types.ORDER_REVERSAL, data);
  } catch (error) {
    return rejectOrderError(error, "Error reversing order");
  }
};
